import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface DaySchedule {
  dow: number;
  start: string;
  end: string;
  enabled: boolean;
}

const DAY_KEYS = ['days.sun', 'days.mon', 'days.tue', 'days.wed', 'days.thu', 'days.fri', 'days.sat'];

const DEFAULT_SCHEDULE: DaySchedule[] = [
  { dow: 0, start: '08:00', end: '17:00', enabled: false },
  { dow: 1, start: '08:00', end: '17:00', enabled: true },
  { dow: 2, start: '08:00', end: '17:00', enabled: true },
  { dow: 3, start: '08:00', end: '17:00', enabled: true },
  { dow: 4, start: '08:00', end: '17:00', enabled: true },
  { dow: 5, start: '08:00', end: '17:00', enabled: true },
  { dow: 6, start: '08:00', end: '17:00', enabled: false },
];

interface ScheduleValue {
  '24_7'?: boolean;
  weekly?: Array<{ dow: number; start: string; end: string }>;
}

interface ScheduleFieldProps {
  field: string;
  defaultValue?: ScheduleValue | null;
  help?: string;
  required?: boolean;
  onSubmit: (value: unknown) => void;
}

function initSchedule(defaultValue?: ScheduleValue | null): { days: DaySchedule[]; is247: boolean } {
  // 24/7 mode
  if (defaultValue && defaultValue['24_7']) {
    return {
      days: DEFAULT_SCHEDULE.map((d) => ({ ...d, enabled: true, start: '00:00', end: '23:59' })),
      is247: true,
    };
  }

  const base = DEFAULT_SCHEDULE.map((d) => ({ ...d }));
  if (defaultValue?.weekly) {
    // Mark all as disabled first, then enable those in default
    for (const day of base) day.enabled = false;
    for (const entry of defaultValue.weekly) {
      const day = base.find((d) => d.dow === entry.dow);
      if (day) {
        day.enabled = true;
        day.start = entry.start;
        day.end = entry.end;
      }
    }
  }
  return { days: base, is247: false };
}

export function ScheduleField({
  field: _field,
  defaultValue,
  help,
  required: _required,
  onSubmit,
}: ScheduleFieldProps): React.ReactElement {
  const { t } = useTranslation();
  const initial = initSchedule(defaultValue);
  const [days, setDays] = useState<DaySchedule[]>(initial.days);
  const [is247, setIs247] = useState(initial.is247);

  const toggleDay = useCallback((dow: number) => {
    setDays((prev) =>
      prev.map((d) => (d.dow === dow ? { ...d, enabled: !d.enabled } : d)),
    );
    setIs247(false);
  }, []);

  const updateTime = useCallback((dow: number, field: 'start' | 'end', value: string) => {
    setDays((prev) =>
      prev.map((d) => (d.dow === dow ? { ...d, [field]: value } : d)),
    );
  }, []);

  const toggle247 = useCallback(() => {
    setIs247((prev) => {
      const next = !prev;
      if (next) {
        setDays((d) => d.map((day) => ({ ...day, enabled: true, start: '00:00', end: '23:59' })));
      } else {
        setDays(initSchedule(defaultValue).days);
      }
      return next;
    });
  }, [defaultValue]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (is247) {
        onSubmit({ '24_7': true });
        return;
      }
      const enabledDays = days.filter((d) => d.enabled);
      onSubmit({
        weekly: enabledDays.map(({ dow, start, end }) => ({ dow, start, end })),
      });
    },
    [days, is247, onSubmit],
  );

  return (
    <form className="sp-field sp-field-schedule" onSubmit={handleSubmit}>
      <label className="sp-schedule-247">
        <input type="checkbox" checked={is247} onChange={toggle247} />
        <span>{t('recipe.alwaysOpen')}</span>
      </label>

      <div className="sp-schedule-grid">
        {days.map((day) => (
          <div
            key={day.dow}
            className={`sp-schedule-row${day.enabled ? ' sp-schedule-row-active' : ''}`}
          >
            <button
              type="button"
              className={`sp-schedule-day${day.enabled ? ' sp-schedule-day-on' : ''}`}
              onClick={() => toggleDay(day.dow)}
            >
              {t(DAY_KEYS[day.dow]!)}
            </button>
            {day.enabled ? (
              <div className="sp-schedule-times">
                <input
                  type="time"
                  className="sp-schedule-time"
                  value={day.start}
                  onChange={(e) => updateTime(day.dow, 'start', e.target.value)}
                />
                <span className="sp-schedule-to">{t('fields.to')}</span>
                <input
                  type="time"
                  className="sp-schedule-time"
                  value={day.end}
                  onChange={(e) => updateTime(day.dow, 'end', e.target.value)}
                />
              </div>
            ) : (
              <span className="sp-schedule-closed">{t('fields.closed')}</span>
            )}
          </div>
        ))}
      </div>

      {help && <span className="sp-field-help">{help}</span>}
      <button className="sp-field-submit" type="submit">
        {t('fields.submit')}
      </button>
    </form>
  );
}
